import { appNames } from '@dvag/dfs-constant-config/app-names';
import { navigate as orchestratorNavigate } from '@dvag/dfs-orchestrator-client/messengers';
import {
  DxCard,
  DxCardContent,
  DxCardHeader,
  DxContainer,
  DxList,
  DxNotificationToast,
  DxText,
} from '@dvag/design-system-react';
import i18next from 'i18next';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ConsentOrLegalGuardianModal } from 'component/ConsentOrLegalGuardianModal/ConsentOrLegalGuardianModal';
import { useGetHouseholdDashboard } from 'hooks/useGetHouseholdDashboard';
import { dashboardUtil } from 'service/model/dashboardUtil';
import { checkIcon, getPersonFullName } from 'utils/util';

import {
  FEEDBACK_COLLABORATION_SECTION,
  DREAM_JOB_SECTION,
  useGetRootSections,
  useGetFeedbackCollaborationList,
  useGetDreamJobList,
} from './pages.config';
import { Item } from '../Card/Item';
import { useGetCustomerPortalStatus } from 'hooks/useGetCustomerPortalStatus';
import { useGdprData } from 'hooks/useGdprData';
import { useBottomCard } from 'hooks/useBottomCard';
import type { SectionListData } from 'type/sectionList';

interface RightCardProps {
  isAdvisory?: boolean;
}

const CLOSE_SECONDS_TIMEOUT = 3;

const { PRIVACY_POLICY, PERSONAL_DATA } = dashboardUtil;

export const RightCard = ({ isAdvisory }: RightCardProps) => {
  const { householdId, section } = useParams();
  const navigate = useNavigate();

  const dashboard = isAdvisory ? 'beratung' : 'analyse';

  const pathToDashboard = `/haushalt/${householdId}/${dashboard}`;

  const householdDashboard = useGetHouseholdDashboard();

  const {
    chosenPerson,
    consentModalVisibility,
    hideConsentModal,
    isConsentEmailSent,
    isConsentFetching,
    personList,
    personListWithConsent,
    setChosenPerson,
    setConsentEmailSentStatus,
    showConsentModal,
  } = useBottomCard({
    householdDashboard,
    section,
  });

  const consentList = useMemo(
    () => householdDashboard.data?.consentList || [],
    [householdDashboard.data?.consentList],
  );

  const customerPortalStatus = useGetCustomerPortalStatus(householdId ?? '');

  const rootSections = useGetRootSections(
    householdId,
    customerPortalStatus.data,
    dashboard,
    personList,
    personListWithConsent,
  );
  const feedbackCollaborationList = useGetFeedbackCollaborationList(householdId);
  const dreamJobList = useGetDreamJobList(householdId);

  const gdprDataList = useGdprData(personList, consentList);

  const goToPerson = (personId: string) =>
    `/haushalt/${householdId}/analyse/persoenliche-daten/${personId}`;

  const personalDataList: SectionListData[] = (householdDashboard.data?.personList || []).map(
    (person, index) => ({
      id: `person-${index}`,
      path: goToPerson(person.id),
      title: getPersonFullName(person),
      icon: 'mensch',
    }),
  );

  const list = {
    defaultSection: rootSections,
    [PERSONAL_DATA]: personalDataList,
    [PRIVACY_POLICY]: gdprDataList,
    [FEEDBACK_COLLABORATION_SECTION]: feedbackCollaborationList,
    [DREAM_JOB_SECTION]: dreamJobList,
  };

  const translation = {
    success: i18next.t('sendConsent.success'),
    collaboration: i18next.t('dashboardCards.collaboration.title'),
    personalData: i18next.t('dashboardCards.collaboration.personalData'),
    dataProtection: i18next.t('sendConsent.dataProtection'),
    feedbackAndCollaboration: i18next.t('dashboardCards.collaboration.feedbackAndCollaboration'),
    dreamJob: i18next.t('dashboardCards.collaboration.dreamJob'),
  };

  const cardTitle = {
    defaultSection: { title: translation.collaboration, dataTestId: 'default-card-title' },
    [PERSONAL_DATA]: { title: translation.personalData, dataTestId: 'personal-data-title' },
    [PRIVACY_POLICY]: { title: translation.dataProtection, dataTestId: 'privacy-policy-title' },
    [FEEDBACK_COLLABORATION_SECTION]: {
      title: translation.feedbackAndCollaboration,
      dataTestId: 'feedback-collaboration-title',
    },
    [DREAM_JOB_SECTION]: { title: translation.dreamJob, dataTestId: 'dream-job-title' },
  };

  const isSectionInItemList = !!section && Object.keys(list).includes(section);
  const sectionKey = isSectionInItemList ? section : 'defaultSection';
  const currentSection = list[sectionKey];

  const onItemClick = (path: string, id?: string | null) => {
    if (section === PERSONAL_DATA) {
      orchestratorNavigate(
        `/${appNames.person}${path}`,
        `/berater/haushalt/${householdId}/analyse/sektion/persoenliche-daten`,
      );
      return;
    }
    if (path === PRIVACY_POLICY && id?.length) {
      const selectedPerson = personList?.find((person) => id === person.id);
      showConsentModal();
      setChosenPerson(selectedPerson);
    }
  };

  return (
    <DxCard style={{ overflow: 'hidden' }} data-testid="right-card">
      {isSectionInItemList ? (
        <DxCardHeader
          label={cardTitle[sectionKey].title}
          icon={checkIcon('pfeil-links')}
          iconcolor="default"
          onClick={() => navigate(pathToDashboard)}
          style={{ cursor: 'pointer' }}
          data-testid={cardTitle[sectionKey].dataTestId}
        />
      ) : (
        <DxCardHeader label={cardTitle[sectionKey].title} />
      )}
      {chosenPerson && (
        <ConsentOrLegalGuardianModal
          visible={consentModalVisibility}
          onClosed={hideConsentModal}
          person={chosenPerson}
          isConsentFetching={isConsentFetching}
          onSubmitSuccess={hideConsentModal}
          setIsConsentEmailSent={setConsentEmailSentStatus}
          personList={personList ?? []}
        />
      )}
      {!!isConsentEmailSent && (
        <DxNotificationToast
          id="consent-email-success"
          data-testid="consent-email-success"
          visible={!!isConsentEmailSent}
          icon={checkIcon('check-kreis')}
          style={{ zIndex: 10000 }}
          closeafter={CLOSE_SECONDS_TIMEOUT}
          onCloseNotification={() => {
            setConsentEmailSentStatus('');
          }}
        >
          <DxContainer className="notification-text-container">
            <DxText>{translation.success}</DxText>
          </DxContainer>
        </DxNotificationToast>
      )}
      <DxCardContent className="dashboard-card-content">
        <DxContainer className="flex column">
          <DxList size="m">
            {currentSection?.map(
              ({ title, icon, path, id, personId, renderRightSection, onClick }) => (
                <Item
                  id={id}
                  key={id}
                  onItemClick={() => (onClick ? onClick() : onItemClick(path, personId))}
                  title={title}
                  icon={icon}
                  renderRightSection={renderRightSection}
                />
              ),
            )}
          </DxList>
        </DxContainer>
      </DxCardContent>
    </DxCard>
  );
};
